import { useState, useMemo } from "react";
import { Users, Trash2, X, Check, Search, Loader2, ArrowLeft } from "lucide-react";
import ScrollArea from "../../components/common/ScrollArea";

function Avatar({ name, size = "md", color }) {
  const initials = name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const sz = {
    sm: "w-6 h-6 text-[9px]",
    md: "w-8 h-8 text-xs",
    lg: "w-10 h-10 text-sm",
  }[size];
  return (
    <div
      className={`${sz} rounded-full flex items-center justify-center font-bold shrink-0`}
      style={{
        background: color ? `${color}20` : "var(--color-bg-overlay)",
        color: color || "var(--color-text-muted)",
        border: `1px solid ${color ? color + "40" : "var(--color-border)"}`,
      }}
    >
      {initials}
    </div>
  );
}

export default function TeamDetailPanel({
  team,
  accent,
  isLoadingDetails,
  orgMembers,
  isLoadingOrgMembers,
  onMemberAdded,
  onMemberRemoved,
  onBack,
}) {
  const [isMembersOpen, setIsMembersOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [draftMemberIds, setDraftMemberIds] = useState([]);
  const [isSavingMembers, setIsSavingMembers] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState(null);

  const members = team?.members ?? [];

  const filteredOrgMembers = useMemo(() => {
    const existingIds = new Set(members.map((m) => m.user_id || m.id));
    const q = memberSearch.trim().toLowerCase();
    return orgMembers.filter((u) => {
      if (existingIds.has(u.user_id)) return false;
      return (
        !q ||
        u.user_name?.toLowerCase().includes(q) ||
        u.username?.toLowerCase().includes(q)
      );
    });
  }, [orgMembers, members, memberSearch]);

  const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getTeamMemberCount = (t) => t.totalMembers ?? t.memberIds?.length ?? 0;

  const handleMembersOpen = () => {
    setDraftMemberIds([]);
    setMemberSearch("");
    setIsMembersOpen(true);
  };

  const handleMembersClose = () => {
    setIsMembersOpen(false);
    setDraftMemberIds([]);
  };

  const handleMemberToggle = (userId) => {
    setDraftMemberIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleMembersSave = async () => {
    if (draftMemberIds.length === 0) {
      handleMembersClose();
      return;
    }
    setIsSavingMembers(true);
    try {
      await onMemberAdded(team.id, draftMemberIds);
      handleMembersClose();
    } finally {
      setIsSavingMembers(false);
    }
  };

  const handleRemoveMember = async (member) => {
    if (removingMemberId) return;
    const userId = member.user_id || member.id;
    setRemovingMemberId(userId);
    try {
      await onMemberRemoved(team.id, member);
    } finally {
      setRemovingMemberId(null);
    }
  };

  if (!team) {
    return (
      <div className="hidden md:flex flex-1 flex-col items-center justify-center gap-3 text-(--color-text-muted)">
        <Users className="w-10 h-10 opacity-20" />
        <p className="text-sm font-medium">Select a team to view details</p>
        <p className="text-xs opacity-60">
          Choose a team from the list on the left
        </p>
      </div>
    );
  }

  return (
    <>
      <ScrollArea className="flex-1 flex flex-col min-h-0">
        {/* ── Mobile back button ── */}
        <div className="md:hidden px-4 pt-4 pb-0">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-xs font-semibold text-(--color-text-muted) hover:text-(--color-text-primary) transition-colors py-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            All Teams
          </button>
        </div>

        {/* ── Hero ── */}
        <div className="px-4 sm:px-8 py-5 sm:py-7 border-b border-(--color-border) bg-(--color-card)/50 shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <div
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center text-base sm:text-lg font-black shrink-0"
              style={{
                background: `${accent}18`,
                color: accent,
                border: `2px solid ${accent}35`,
              }}
            >
              {team.name.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-black text-(--color-text-primary)">
                {team.name}
              </h2>
              <p className="text-sm text-(--color-text-primary) mt-1 leading-relaxed max-w-lg">
                {team.description ||
                  "A shared space for this team's work and updates"}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-(--color-bg-card) border border-(--color-border) text-(--color-text-muted)">
                  <Users className="w-3 h-3" />
                  {getTeamMemberCount(team)} member
                  {getTeamMemberCount(team) !== 1 ? "s" : ""}
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-(--color-bg-card) border border-(--color-border) text-(--color-text-muted)">
                  {team.createdByName || "—"}
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-(--color-bg-card) border border-(--color-border) text-(--color-text-muted)">
                  {formatDate(team.createdAt)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Members section ── */}
        <div className="flex-1 px-4 sm:px-6 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <p className="text-[9px] font-black uppercase text-(--color-text-primary)">
              Members · {members.length}
            </p>
            <button
              onClick={handleMembersOpen}
              className="text-[11px] font-bold text-(--color-primary) hover:underline self-start sm:self-auto"
            >
              + Add Members
            </button>
          </div>

          {isLoadingDetails ? (
            <div className="flex flex-col items-center justify-center py-16 rounded-2xl border border-dashed border-(--color-border)">
              <Loader2 className="w-6 h-6 text-(--color-text-muted) mb-2 opacity-40 animate-spin" />
              <p className="text-sm text-(--color-text-muted)">
                Loading team members...
              </p>
            </div>
          ) : members.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 rounded-2xl border border-dashed border-(--color-border)">
              <Users className="w-8 h-8 text-(--color-text-muted) mb-2 opacity-30" />
              <p className="text-sm text-(--color-text-muted)">No members yet</p>
              <button
                onClick={handleMembersOpen}
                className="mt-2 text-xs text-(--color-primary) hover:underline font-semibold"
              >
                Add your first member →
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {members.map((member, i) => {
                const userId = member.user_id || member.id;
                const isRemoving = removingMemberId === userId;
                return (
                  <div
                    key={member.id}
                    className="flex items-center gap-4 px-4 py-3 rounded-lg border border-(--color-border) bg-(--color-bg-card) hover:bg-(--color-bg-overlay) transition-all"
                  >
                    <span className="text-[10px] text-(--color-text-muted) w-4 font-mono font-bold tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <Avatar name={member.name} size="md" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-(--color-text-primary) truncate block">
                        {member.name}
                      </span>
                      <span className="text-xs text-(--color-text-muted) truncate block">
                        {member.title}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveMember(member)}
                      disabled={!!removingMemberId}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400/60 hover:text-red-400 hover:bg-red-500/10 transition-all disabled:cursor-not-allowed disabled:opacity-40"
                      title="Remove from team"
                    >
                      {isRemoving ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* ── Add Members Modal ── */}
      {isMembersOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-(--color-bg) rounded-2xl shadow-2xl w-full max-w-xl border border-(--color-border) overflow-hidden">
            <div className="flex items-center justify-between px-5 sm:px-6 py-5 border-b border-(--color-border)">
              <div>
                <h2 className="text-sm font-bold text-(--color-text-primary)">
                  Add Members
                </h2>
                <p className="text-xs text-(--color-text-muted) mt-0.5">
                  {team.name} · {draftMemberIds.length} selected
                </p>
              </div>
              <button
                onClick={handleMembersClose}
                disabled={isSavingMembers}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-(--color-text-muted) hover:bg-(--color-bg-overlay) transition-colors disabled:opacity-50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 sm:px-6 py-4 space-y-3">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-(--color-text-muted)" />
                <input
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  placeholder="Search by name or username..."
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-(--color-border) rounded-lg bg-(--color-bg-card) text-(--color-text-primary) placeholder:text-(--color-text-muted)/40 focus:outline-none focus:ring-2 focus:ring-(--color-primary)/30 focus:border-(--color-primary) transition-all"
                />
              </div>

              <ScrollArea className="max-h-72 pr-1 flex flex-col gap-1.5">
                {isLoadingOrgMembers ? (
                  <div className="flex items-center justify-center py-10 gap-2 text-(--color-text-muted)">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Loading members...</span>
                  </div>
                ) : filteredOrgMembers.length === 0 ? (
                  <div className="flex items-center justify-center py-10">
                    <span className="text-sm text-(--color-text-muted)">
                      {orgMembers.length > 0 &&
                      orgMembers.length === members.length
                        ? "All organization members are already in this team"
                        : "No members found"}
                    </span>
                  </div>
                ) : (
                  filteredOrgMembers.map((user) => {
                    const isSelected = draftMemberIds.includes(user.user_id);
                    return (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => handleMemberToggle(user.user_id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-all ${
                          isSelected
                            ? "border-(--color-primary)/40 bg-(--color-primary)/8"
                            : "border-(--color-border) hover:bg-(--color-bg-overlay)"
                        }`}
                      >
                        <Avatar
                          name={user.user_name}
                          size="md"
                          color={isSelected ? "var(--color-primary)" : undefined}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-(--color-text-primary) truncate">
                            {user.user_name}
                          </div>
                          <div className="text-xs text-(--color-text-muted) truncate">
                            @{user.username}
                          </div>
                        </div>
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                            isSelected
                              ? "bg-(--color-primary) border-(--color-primary)"
                              : "border-(--color-border)"
                          }`}
                        >
                          {isSelected && (
                            <Check className="w-3 h-3 text-white dark:text-black" />
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </ScrollArea>
            </div>

            <div className="px-5 sm:px-6 py-4 border-t border-(--color-border) flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleMembersClose}
                disabled={isSavingMembers}
                className="flex-1 py-2.5 border border-(--color-border) text-(--color-text-primary) rounded-lg hover:bg-(--color-bg-overlay) transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleMembersSave}
                disabled={isSavingMembers}
                className="flex-1 py-2.5 bg-(--color-primary) text-white dark:text-black rounded-lg hover:opacity-90 transition-all text-sm font-bold disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSavingMembers ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}